import { Table } from 'semantic-ui-react';
import { AvailableRoom } from './AvailableRoom';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AddRoomBookingFormValues } from './validation';
import { LrwRoomDto } from 'api/planning';
import { useMemo } from 'react';

type Props = {
  availableRooms: LrwRoomDto[];
  searchTerm: string | undefined;
};

export const AvailableRoomsTable = ({ availableRooms, searchTerm }: Props) => {
  const { control } = useFormContext<AddRoomBookingFormValues>();

  const { fields: rooms } = useFieldArray({
    control,
    name: 'rooms',
  });

  const roomsById = useMemo(() => new Map(availableRooms.map((r) => [r.id, r])), [availableRooms]);
  const hasAnyClashes = useMemo(
    () => availableRooms.some((room) => room.clashes?.length > 0),
    [availableRooms]
  );

  const normalizedSearch = (searchTerm ?? '').trim().toLowerCase();
  const shouldFilter = normalizedSearch.length >= 2;

  const rows = useMemo(() => {
    return rooms
      .filter((x) => {
        const room = roomsById.get(x.roomId);
        if (!room) return false;
        if (!shouldFilter) return true;

        return (
          room.buildingName.toLowerCase().includes(normalizedSearch) ||
          room.floor.toLowerCase().includes(normalizedSearch) ||
          room.name.toLowerCase().includes(normalizedSearch)
        );
      })
      .map((x, i) => {
        const room = roomsById.get(x.roomId);
        if (!room) return null;

        return <AvailableRoom room={room} id={i} key={x.roomId} showClashes={hasAnyClashes} />;
      })
      .filter(Boolean);
  }, [rooms, roomsById, shouldFilter, normalizedSearch, hasAnyClashes]);

  return (
    <Table
      celled
      striped
      size="small"
      style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      data-testid="available-rooms-table-lrw"
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={1} />
          <Table.HeaderCell colSpan={hasAnyClashes ? 2 : 1}>Building</Table.HeaderCell>
          <Table.HeaderCell>Floor</Table.HeaderCell>
          <Table.HeaderCell>Room</Table.HeaderCell>
          <Table.HeaderCell>Seat limit</Table.HeaderCell>
          <Table.HeaderCell>Planned seats</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  );
};
